import React from "react";
import { ReCaptchaProvider } from "@wss/re-captcha";
import { ProductDetailPageResult, Templates } from "../ProductDetailPageResult";
import ProductDetailProductAds from "../ProductDetailProductAds";
import useIsMounted from "~/common/useIsMounted";
import SpecificationTable from "../SpecificationTable";
import ProductDescription from "../ProductDescription";
import QuestionsAndAnswers from "../QuestionsAndAnswers/QuestionsAndAnswers";
import Certifications from "../Certifications";
import VideoSpotlight from "../VideoSpotlight";
import OutletNoticeDescription from "./OutletTemplate/OutletNoticeDescription";
import Warnings from "../Warnings";
import ChatbotQuestionsAndAnswers from "../QuestionsAndAnswers/ChatbotQuestionsAndAnswers";

interface StandardTemplateDetailSectionProps {
  activeTemplate: ProductDetailPageResult;
}

const StandardTemplateDetailSection = ({
  activeTemplate,
}: StandardTemplateDetailSectionProps) => {
  const mounted = useIsMounted();

  const {
    certifications,
    itemNumber,
    isCommonCarrier,
    videos,
    productAds,
    specificationTable,
    productDisclaimer,
    manufacturerItemNumberDisplayText = "",
    extendedDescription,
    questionsAndAnswers,
    noticesAndWarnings,
    template,
    outletAttributes,
    unitOfMeasure,
    unitsPerPackaging,
    showEverlivingProductPage,
    weightInPounds,
    itemNumberId,
  } = activeTemplate;

  const isVirtualGroupMaster =
    activeTemplate.groupingAttributes.isGroupMaster &&
    activeTemplate.groupingAttributes.isVirtual;

  const isOutletTemplate = template === Templates.OutletTemplate;
  const isEverlivingOrOutlet = isOutletTemplate || showEverlivingProductPage;

  const videosToSpotlight = videos?.length
    ? videos.filter((video) => !video.isModelingVideo)
    : [];

  return (
    <div className="box clearfix mt-1 xm:mt-0">
      <div className="padded">
        {productAds && <ProductDetailProductAds ads={productAds} />}
        {isOutletTemplate && <OutletNoticeDescription />}
        {mounted && videosToSpotlight?.length > 0 && (
          <VideoSpotlight videos={videosToSpotlight} autoPlayOnVideoSwap />
        )}
        <SpecificationTable
          itemNumber={itemNumber}
          specifications={specificationTable?.specifications ?? []}
          name={specificationTable?.name ?? ""}
          unitOfMeasure={unitOfMeasure}
          unitsPerPackaging={unitsPerPackaging}
          showInSidebar={false}
          weightInPounds={weightInPounds}
        />
        {extendedDescription && extendedDescription?.length && (
          <ProductDescription
            header={
              isVirtualGroupMaster ? "" : manufacturerItemNumberDisplayText
            }
            description={extendedDescription}
            isCommonCarrier={isCommonCarrier}
            noticesAndWarnings={noticesAndWarnings}
            isOutletTemplate={isOutletTemplate}
            outletHighlights={outletAttributes?.highlights}
            showEverlivingProductPage={showEverlivingProductPage}
          />
        )}
        <div className="flex flex-col inline-flex">
          <Warnings
            isStandardTemplate
            prop65Warning={productDisclaimer?.prop65Warning}
            displayAb1200Disclaimer={productDisclaimer?.displayAb1200Disclaimer}
            ab1200Chemicals={productDisclaimer?.ab1200Chemicals}
            spanishAb1200Chemicals={productDisclaimer?.spanishAb1200Chemicals}
          />
        </div>
      </div>
      {certifications && certifications?.length > 0 && (
        <Certifications certifications={certifications} />
      )}
      {itemNumberId > 0 && !isEverlivingOrOutlet && (
        <div
          id="customerQA"
          className="border-t-[1px] border-solid border-[#DBDFD8] border-x-0 border-b-0 scroll-mt-[100px]"
          tabIndex={-1}
        >
          <ReCaptchaProvider>
            <ChatbotQuestionsAndAnswers
              itemNumberId={itemNumberId}
              itemNumber={itemNumber}
              isExpandedDescriptionTemplate={false}
            />
          </ReCaptchaProvider>
          {questionsAndAnswers?.length > 0 && (
            <QuestionsAndAnswers questionsAndAnswers={questionsAndAnswers} />
          )}
        </div>
      )}
    </div>
  );
};

export default StandardTemplateDetailSection;
